import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Quote, Wind, Compass, Sunrise } from "lucide-react";
import StorySubmit from "@/components/StorySubmit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "The Formula in Action" — until the Founder's Circle graduates, we show exactly
// how each module attacks a symptom. NO invented customer quotes.
const formulaInAction = [
  {
    from: "Dreading the clock-in",
    to: "Walking in grounded",
    module: "Module 02 · Accepting Where You Are",
    how: "Wherever the shift puts you, God put you there. Stop bracing against the day, take responsibility for the next hour — and walk in with your feet under you.",
  },
  {
    from: "Burning out",
    to: "Mastering resilience",
    module: "Module 04 · The Wall of Adversity",
    how: "Your fears, your traumas, the weight only you and God know about. You cannot go over it or around it — Module 4 teaches you to go through the wall instead of burning out against it.",
    featured: true,
  },
  {
    from: "Carrying it home",
    to: "Setting it down",
    module: "Module 07 · Wisdom — Processing Your Pain",
    how: "Move from raw emotion to wisdom. See what happened in a clearer light — so the hard exchange stops riding home in the car with you.",
  },
  {
    from: "Quitting in month six",
    to: "Still standing in year one",
    module: "Module 08 · Understanding — Becoming the Healer",
    how: "It was never random pain — it was preparation. You leave able to hold your own weight, and to help the person next to you hold theirs.",
  },
];

// "What we measure" — the outcomes each module family is DESIGNED to move.
// No invented client numbers. Direction of change only.
const measures = [
  {
    icon: Wind,
    label: "Pre-shift regulation",
    outcome: "Measurable calming of the nervous system before the shift begins.",
    signal: "HRV & breath rate ↑ toward baseline",
  },
  {
    icon: Compass,
    label: "Accepting where you are",
    outcome: "Workers name the specific weights they've been carrying quietly.",
    signal: "Self-reported clarity ↑",
  },
  {
    icon: Sunrise,
    label: "Micro-recovery in-shift",
    outcome: "Small resets that keep energy from collapsing in the last hours.",
    signal: "End-of-shift fatigue ↓",
  },
];

export default function Testimonials() {
  const [stories, setStories] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/stories/approved`).then((r) => {
      if (!alive) return;
      setStories(Array.isArray(r.data?.stories) ? r.data.stories : []);
      setLoaded(true);
    }).catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  const hasReal = stories.length > 0;
  const notes = hasReal
    ? stories.slice(0, 3).map((s, i) => ({
        quote: s.quote,
        source: [s.name, s.role].filter(Boolean).join(" · "),
        featured: i === 0,
      }))
    : [];

  return (
    <section
      data-testid="field-notes-section"
      className="relative bg-[#FFFFFF] py-28 md:py-36 border-y border-[#E7DFD5]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        {/* Field Notes — real approved stories if we have them, else doctrine excerpts */}
        <div className="grid grid-cols-12 gap-6 mb-14">
          <div className="col-span-12 md:col-span-7">
            <div className="section-tag mb-6">{hasReal ? "§ Field Notes" : "§ The Formula in Action"}</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              {hasReal
                ? <>Stories from the <span className="italic text-[#B45309]">field</span>.</>
                : <>Built to break <span className="italic text-[#B45309]">specific</span> walls.</>}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 flex items-end">
            <p className="text-[#78716C] text-sm leading-relaxed max-w-md" data-testid="field-notes-caption">
              {hasReal
                ? "Real workers, real words. Every story below was submitted and approved. Send us yours below."
                : "No borrowed testimonials. Until our Founder's Circle graduates, here is exactly how each module attacks a symptom. Their real stories will take this space."}
            </p>
          </div>
        </div>

        {hasReal ? (
          <div className="grid grid-cols-12 gap-6 md:gap-8" data-testid="field-notes-real">
            {notes.map((t, i) => (
              <motion.blockquote
                key={t.quote}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, delay: i * 0.1, ease: [0.65, 0, 0.35, 1] }}
                className={`col-span-12 ${
                  t.featured ? "md:col-span-6" : "md:col-span-3"
                } tacc-card p-8 md:p-10 flex flex-col justify-between min-h-[280px]`}
                data-testid={`field-note-${i}`}
              >
                <Quote size={28} className="text-[#B45309]" strokeWidth={1.2} />
                <p
                  className={`font-display leading-[1.15] tracking-tight my-6 ${
                    t.featured ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"
                  }`}
                >
                  {`“${t.quote}”`}
                </p>
                <cite className="not-italic font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C]">
                  — {t.source}
                </cite>
              </motion.blockquote>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-6 md:gap-8" data-testid="formula-in-action">
            {formulaInAction.map((f, i) => (
              <motion.div
                key={f.module}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, delay: i * 0.1, ease: [0.65, 0, 0.35, 1] }}
                className={`col-span-12 md:col-span-6 tacc-card p-8 md:p-10 ${f.featured ? "border-[#F59E0B]/40" : ""}`}
                data-testid={`formula-box-${i}`}
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-5 font-mono text-[10px] tracking-[0.25em] uppercase">
                  <span className="text-[#78716C] line-through decoration-[#F59E0B]/60">{f.from}</span>
                  <span className="text-[#B45309]">→</span>
                  <span className="text-[#B45309]">{f.to}</span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-4">{f.module}</h3>
                <p className="text-[#78716C] text-sm md:text-base leading-relaxed">{f.how}</p>
              </motion.div>
            ))}
          </div>
        )}

        {/* Send us your story submission form */}
        {loaded && <StorySubmit />}

        {/* What We Measure — design intent, honest signals, no invented numbers */}
        <div className="grid grid-cols-12 gap-6 mb-14 mt-24 md:mt-32">
          <div className="col-span-12 md:col-span-7">
            <div className="section-tag mb-6">§ What we measure</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              What the program is <span className="italic text-[#B45309]">built</span> to move.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 flex items-end">
            <p className="text-[#78716C] text-sm leading-relaxed max-w-md">
              Direction of change, not fabricated percentages. Independent measurement is included in Team and Enterprise engagements.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6" data-testid="measure-grid">
          {measures.map((m, i) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={m.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, delay: i * 0.1, ease: [0.65, 0, 0.35, 1] }}
                className="col-span-12 md:col-span-4 border border-[#E7DFD5] p-8 md:p-10 flex flex-col min-h-[260px] hover:border-[#F59E0B]/50 transition-colors duration-500"
                data-testid={`measure-tile-${i}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <Icon size={26} className="text-[#B45309]" strokeWidth={1.4} />
                  <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#A8A29E]">
                    /0{i + 1}
                  </span>
                </div>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mb-3">
                  {m.label}
                </div>
                <p className="font-display text-2xl md:text-3xl leading-tight tracking-tight mb-6">
                  {m.outcome}
                </p>
                <div className="mt-auto pt-6 border-t border-[#E7DFD5] font-mono text-[11px] tracking-[0.2em] uppercase text-[#B45309]">
                  {m.signal}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
